import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Typography, Space, Segmented } from 'antd';
import {
  ShoppingOutlined,
  AppstoreOutlined,
  OrderedListOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { colors } from '@/styles/dark-theme';
import { useSupplier } from '@/context/SupplierContext';
import { SUPPLIER_LABELS, type Supplier } from '@/types/supplier';

const { Sider, Content, Header } = Layout;
const { Text, Title } = Typography;

const menuItems = [
  {
    key: '/products',
    icon: <ShoppingOutlined />,
    label: '产品浏览',
  },
  {
    key: '/listings',
    icon: <AppstoreOutlined />,
    label: '上架管理',
  },
  {
    key: '/orders',
    icon: <OrderedListOutlined />,
    label: '订单监控',
  },
  {
    key: '/settings',
    icon: <SettingOutlined />,
    label: '系统设置',
  },
];

const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { supplier, setSupplier } = useSupplier();

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const selectedKey =
    menuItems.find((item) => location.pathname.startsWith(item.key))?.key || '/products';

  const currentTitle = menuItems.find(item => item.key === selectedKey)?.label || '产品浏览';

  return (
    <Layout style={{ minHeight: '100vh', background: 'transparent' }}>
      {/* 侧栏 - 彻底还原 Figma 半透明胶囊感 */}
      <Sider
        theme="dark"
        width={240}
        style={{
          background: 'rgba(10, 14, 39, 0.6)',
          borderRight: `1px solid ${colors.border}`,
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
          backdropFilter: 'blur(30px)',
        }}
      >
        {/* Logo 区域 */}
        <div
          style={{
            height: 80,
            display: 'flex',
            alignItems: 'center',
            padding: '0 24px',
            marginBottom: 20,
          }}
        >
          <Space align="center" size={12}>
            <div style={{
              width: 40,
              height: 40,
              background: 'linear-gradient(135deg, #4f6ef7 0%, #3b5ad6 100%)',
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 15px rgba(79, 110, 247, 0.4)',
            }}>
              <img
                src="/logo-192.png"
                alt="logo"
                style={{ width: 28, height: 28, filter: 'brightness(0) invert(1)' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Text
                strong
                style={{
                  fontSize: 16,
                  color: '#fff',
                  lineHeight: 1.2,
                  letterSpacing: 0.5,
                }}
              >
                holodao 产品中心
              </Text>
              <Text style={{ fontSize: 10, color: colors.textSecondary }}>
                Supplier Integration Hub
              </Text>
            </div>
          </Space>
        </div>

        {/* 菜单 - 样式已在 index.css 定义 */}
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{
            background: 'transparent',
            borderRight: 0,
          }}
        />

        {/* 底部版本 */}
        <div style={{ position: 'absolute', bottom: 24, left: 24 }}>
          <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.2)' }}>
            v1.0.0
          </Text>
        </div>
      </Sider>

      <Layout style={{ marginLeft: 240, background: 'transparent' }}>
        {/* 顶栏 - 还原 Figma */}
        <Header
          style={{
            height: 80,
            background: 'rgba(255, 255, 255, 0.02)',
            backdropFilter: 'blur(10px)',
            borderBottom: `1px solid ${colors.border}`,
            padding: '0 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 90,
          }}
        >
          <Space size={12}>
            <div style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: `1px solid ${colors.accent}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.accent,
            }}>
              <AppstoreOutlined style={{ fontSize: 16 }} />
            </div>
            <div>
              <Title level={4} style={{ margin: 0, color: '#fff', fontSize: 18 }}>{currentTitle}</Title>
              <Text style={{ fontSize: 12, color: colors.textSecondary }}>
                浏览器API产品，选择感兴趣的产品上架到您的 Shopify 店铺
              </Text>
            </div>
          </Space>

          <Space size={20}>
            <Segmented<Supplier>
              value={supplier}
              options={[
                { label: SUPPLIER_LABELS.giga, value: 'giga' },
                { label: SUPPLIER_LABELS.aosom, value: 'aosom' },
              ]}
              onChange={(value) => {
                setSupplier(value);
                if (location.pathname.startsWith('/products')) {
                  navigate('/products');
                } else if (location.pathname.startsWith('/listings')) {
                  navigate('/listings');
                }
              }}
            />
            <div style={{
              background: 'rgba(255,255,255,0.05)',
              padding: '4px 16px',
              borderRadius: 20,
              border: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
              <UserOutlined style={{ color: colors.textSecondary }} />
              <Text style={{ color: '#fff', fontSize: 13, fontWeight: 500 }}>USER NAME</Text>
            </div>
          </Space>
        </Header>

        <Content
          style={{
            padding: '32px',
            minHeight: 'calc(100vh - 80px)',
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
